import React, { useState, useEffect } from 'react';
import { Radio, Input, Button } from 'antd'; // Import Button
import { SearchOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { isDate } from 'moment';

const GroupContent = ({ selectedGroup, setSelectedGroup }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isClearVisible, setIsClearVisible] = useState(false);
  const [selectedDateFilter, setSelectedDateFilter] = useState(null);

  const handleClear = () => {
    setSelectedGroup('None');
    setSelectedDateFilter(null); // Also clear the nested filter
  };

  useEffect(() => {
    setIsClearVisible(selectedGroup !== 'None' && selectedGroup !== null);
  }, [selectedGroup]);

  // Handler for the main group selection
  const handleMainGroupChange = (e) => {
    const newGroup = e.target.value;
    setSelectedGroup(newGroup);
    // Reset nested filter if a non-date item is selected
    const isDateKey = allAttributes.find(attr => attr.key === newGroup)?.isDate;
    if (!isDateKey) {
      setSelectedDateFilter(null);
    }
  };

  const handleDateFilterChange = (e) => {
    const newDateFilter = e.target.value;
    setSelectedDateFilter(newDateFilter);
    setSelectedGroup(`${selectedGroup.split('_')[0]}_${newDateFilter}`);
  };

  const allAttributes = [
    { key: 'None', label: 'None' },
    { key: 'initiativeNumber', label: '#', group: 'General' },
    { key: 'initiativeName', label: 'Name', group: 'General' },
    { key: 'initiativeNumber - initiativeName', label: '# - Name', group: 'General' },
    { key: 'weeklyStatus', label: 'Weekly status', group: 'General' },
    { key: 'AllowOnlyTo', label: 'Access', group: 'General' },
    { key: 'workstreamSponsorWorkstream', label: 'Accountable Workstream', group: 'General' },
    { key: 'workstreamLeadSubworkstream', label: ' Subworkstream', group: 'General' },
    {
      key: 'crossFunctionalSupportWorkstream',
      label: 'Cross Functional Workstream Support',
      group: 'General',
    },
    { label: 'Sector', key: 'sector', group: 'General' },
    { label: 'Subsector', key: 'subsector', group: 'General' },
    { label: 'Type', key: 'type', group: 'General' },
    { label: 'Initiative Category', key: 'initiativeCategory', group: 'General' },
    { label: 'Size', key: 'size', group: 'General' },
    { label: 'Segment', key: 'segment', group: 'General' },
    { key: 'Description', label: 'Description', group: 'General' },
    {
      key: 'selectedForToMeetingDescusion',
      label: 'Select To meeting descussion',
      group: 'General',
    },
    { key: 'stage', label: 'Stage', group: 'Stage' },
    { key: 'Stage (simplified)', label: 'Stage (simplified)', group: 'Stage' },
    {
      key: 'workstreamLeadPendingApproval',
      label: 'Workstream Lead pending approval?',
      group: 'Stage',
    },
    {
      key: 'workstreamSponsorPendingApproval',
      label: 'Workstream Sponsor pending approval?',
      group: 'Stage',
    },
    {
      key: 'financeLeadPendingApproval',
      label: 'Finance lead pending approval?',
      group: 'Stage',
    },
    {
      key: 'Strategy analyst pending approval?',
      label: 'Strategy analyst pending approval?',
      group: 'Stage',
    },
    { key: 'initiativeOwnerInfo', label: 'Initiative Owner', group: 'Team' },
    { key: 'initiativeOwnerBackupInfo', label: 'Initiative Owner (back-up)', group: 'Team' },
    { key: 'workstreamLeadInfo', label: 'Workstream Lead', group: 'Team' },
    { key: 'workstreamSponsorInfo', label: 'Workstream Sponsor', group: 'Team' },
    { key: 'TO Finance Lead', label: 'TO Finance Lead', group: 'Team' },
    {
      key: 'netRecurringBenefitsRange',
      label: 'Net recurring benefits range (latest estimate, annualized)',
      group: 'Impact summary',
    },
    {
      key: 'actualImpactUpdateCheck',
      label: 'Actual impact update check (for last completed and previous periods)',
      group: 'Impact summary',
    },
    {
      label: '(FY23-24) Actual impact update check',
      key: '(FY23-24) Actual impact update check',
      group: 'Impact summary',
    },
    {
      label: '(FY24-25) Actual impact update check',
      key: '(FY24-25) Actual impact update check',
      group: 'Impact summary',
    },
    { key: 'planningSummary', label: 'Planning summary', group: 'Timing information' },
    {
      key: 'Planning summary (simplified)',
      label: 'Planning summary (simplified)',
      group: 'Timing information',
    },
    {
      key: 'L3 date compliance check (forecast vs planned date)',
      label: 'L3 date compliance check (forecast vs planned date)',
      group: 'Timing information',
    },
    {
      key: 'L4 date compliance check (forecast vs planned date)',
      label: 'L4 date compliance check (forecast vs planned date)',
      group: 'Timing information',
    },
    // Dates with a new `isDate: true` flag
    { key: 'L1 Original Planned Date', label: 'L1 Original Planned Date', group: 'Dates', isDate: true },
    { key: 'L2 Original Planned Date', label: 'L2 Original Planned Date', group: 'Dates', isDate: true },
    { key: 'L3 Original Planned Date', label: 'L3 Original Planned Date', group: 'Dates', isDate: true },
    { key: 'L4 Original Planned Date', label: 'L4 Original Planned Date', group: 'Dates', isDate: true },
    { key: 'L5 Original Planned Date', label: 'L5 Original Planned Date', group: 'Dates', isDate: true },
    { key: 'l1PlannedDate', label: 'L1 planned date', group: 'Dates', isDate: true },
    { key: 'l2ForecastDate', label: 'L2 Planned date', group: 'Dates', isDate: true },
    { key: 'l3PlannedDate', label: 'L3 planned date', group: 'Dates', isDate: true },
    { key: 'l4PlannedDate', label: 'L4 planned date', group: 'Dates', isDate: true },
    { key: 'l5ForecastDate', label: 'L5 planned date', group: 'Dates', isDate: true },
    { key: 'l0ActualDate', label: 'L0 actual date', group: 'Dates', isDate: true },
    { key: 'l1ActualDate', label: 'L1 actual date', group: 'Dates', isDate: true },
    { key: 'l2ActualDate', label: 'L2 actual date', group: 'Dates', isDate: true },
    { key: 'l3ActualDate', label: 'L3 actual date', group: 'Dates', isDate: true },
    { key: 'l4ActualDate', label: 'L4 actual date', group: 'Dates', isDate: true },
    { key: 'l5ActualDate', label: 'L5 actual date', group: 'Dates', isDate: true },
    { key: 'latestActualDateAllLGates', label: 'Latest actual date (All L gates)', group: 'Dates', isDate: true },
    { key: 'subForL1ApprovDate', label: 'Sub. for L1 approv. date', group: 'Dates', isDate: true },
    { key: 'subForL2ApprovDate', label: 'Sub. for L2 approv. date', group: 'Dates', isDate: true },
    { key: 'subForL3ApprovDate', label: 'Sub. for L3 approv. date', group: 'Dates', isDate: true },
    { key: 'subForL4ApprovDate', label: 'Sub. for L4 approv. date', group: 'Dates', isDate: true },
    { key: 'subForL5ApprovDate', label: 'Sub. for L5 approv. date', group: 'Dates', isDate: true },
    { key: 'cancellationDate', label: 'Cancellation date', group: 'Dates', isDate: true },
    {
      key: 'actualKpiUpdateCheck',
      label: 'Actual KPI update check (for last completed and previous periods)',
      group: 'KPI summary',
    },
    {
      key: 'UniversalKPI',
      label: 'Universal KPI',
      group: 'KPI summary'
    },
    {
      key: 'pipelineWeeklyVarianceL0Plus',
      label: 'Pipeline weekly variance (L0+)',
      group: 'Change (current vs last week)',
    },
    {
      key: 'pipelineWeeklyVarianceL1Plus',
      label: 'Pipeline weekly variance (L1+)',
      group: 'Change (current vs last week)',
    },
    {
      key: 'pipelineWeeklyVarianceL2Plus',
      label: 'Pipeline weekly variance (L2+)',
      group: 'Change (current vs last week)',
    },
    {
      key: 'pipelineWeeklyVarianceL3Plus',
      label: 'Pipeline weekly variance (L3+)',
      group: 'Change (current vs last week)',
    },
    {
      key: 'pipelineWeeklyVarianceL4Plus',
      label: 'Pipeline weekly variance (L4+)',
      group: 'Change (current vs last week)',
    },
    { key: 'Number of Attachment', label: 'Number of Attachment', group: 'Other' },
    { key: 'Has qualitative KPI?', label: 'Has qualitative KPI?', group: 'Other' },
    { key: 'hasFinancialImpact', label: 'Has financial impacts?', group: 'Other' },
    { key: 'forSpecificCustomer', label: 'Is Customer Related?', group: 'Other' },
    { key: 'segmentalPBT', label: 'Segmental PBT', group: 'Other' },
    {
      key: 'Future implementation costs range',
      label: 'Future implementation costs range',
      group: 'Sequencing',
    },
  ];
  const filteredAttributes = allAttributes.filter(
    (attr) =>
      attr.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (attr.group && attr.group.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  const groupedAttributes = {};
  filteredAttributes.forEach((attr) => {
    if (attr.group) {
      if (!groupedAttributes[attr.group]) {
        groupedAttributes[attr.group] = [];
      }
      groupedAttributes[attr.group].push(attr);
    } else {
      if (!groupedAttributes['']) {
        groupedAttributes[''] = [];
      }
      groupedAttributes[''].push(attr); // Add ungrouped
    }
  });

  return (
    <div style={{ padding: '10px', maxHeight: '500px', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
        <Input
          placeholder="Search attributes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ marginRight: '8px', flex: 1 }}
          prefix={<SearchOutlined />}
        />
        {isClearVisible && (
          <Button
            type="text"
            onClick={handleClear}
            style={{ padding: 0, color: 'blue' }}
            icon={<CloseCircleOutlined />}
          >
            Clear
          </Button>
        )}
      </div>
      <Radio.Group onChange={handleMainGroupChange} value={selectedGroup}>
        {Object.entries(groupedAttributes).map(([groupName, attributes]) => (
          <div key={groupName}>
            {groupName && (
              <div style={{ fontWeight: 'bold', marginTop: '10px', fontSize: 14, color: '#333' }}>
                {groupName}
              </div>
            )}
            {attributes.map((attr) => (
              <div key={attr.key}>
                <Radio value={attr.key} style={{ display: 'block' }}>
                  {attr.label}
                </Radio>
                {attr.isDate && selectedGroup?.startsWith(attr.key) && (
                  <div style={{ paddingLeft: '20px', marginTop: '5px' }}>
                    <Radio.Group onChange={handleDateFilterChange} value={selectedDateFilter}>
                      <Radio value="Year" style={{ display: 'block' }}>Year</Radio>
                      <Radio value="Quarter" style={{ display: 'block' }}>Quarter</Radio>
                      <Radio value="Month" style={{ display: 'block' }}>Month</Radio>
                      <Radio value="Week" style={{ display: 'block' }}>Week</Radio>
                      <Radio value="Day" style={{ display: 'block' }}>Day</Radio>
                    </Radio.Group>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </Radio.Group>
    </div>
  );
};

export default GroupContent;